<template>
	<div class="min-donation-view">
		<stepper
			v-bind:steps="['Account', 'Cause', 'Preferences', 'Funding']"
			:currentStep="3"
			v-if="onboarding"
		/>
		<screen class="min-donation-view" type="blue" :institution="true">
			<template slot="logo">
				<img
					class="header-logo"
					:src="this.cause.icon"
					:style="{ padding: this.cause.type == 'group' && '5px' }"
				/>
			</template>
			<template slot="org_name">
				<p style="font-size: 16px; color: white; margin-top: 3px">
					{{ this.cause.name }}
				</p>
			</template>
			<template slot="header">
				<ui-header
					type="light"
					@click="onBack"
					v-if="cause.type !== 'group'"
				>
					<h2 slot="title">Set Your Goal</h2>
					<p class="subtitle">
						How much will you RoundUp? Set a minimum goal amount you
						will donate each month.
					</p>
				</ui-header>
				<ui-header v-else type="light" @click="onLogout" action="close">
					<h2 slot="title">Set Your Goal</h2>
					<p class="subtitle">
						How much will you RoundUp? Set a minimum goal amount you
						will donate each month.
					</p>
				</ui-header>
			</template>
			<form v-on:submit.prevent="onSubmit">
				<vue-slider
					ref="slider"
					v-model="minMax"
					:min="1"
					:max="sliderMax"
					:interval="1"
					:enable-cross="false"
					:marks="marks"
					style="
						padding: none;
						height: 48px;
						margin: 20px 20px;
						margin-right: 30px;
					"
					class="roundup-slider"
					:tooltipStyle="{ color: 'white' }"
					:tooltip-formatter="moneyFormat"
					@error="error"
					@change="clearErrorMsg"
					@dragging="(value, idx) => trackChange(idx)"
				>
					<template v-slot:dot>
						<img src="@/assets/slider_dot.svg" class="slider-dot" />
					</template>
				</vue-slider>
				<div class="roundup-row" style="margin-top: 30px">
					<label style="margin-right: 10px">
						<ui-input
							type="blue"
							placeholder="Minimum"
							v-model="minGoal"
							@input="clearErrorMsg"
						/>
					</label>
					<label style="margin-left: 10px">
						<ui-input
							type="blue"
							placeholder="Max (Optional)"
							v-model="maxGoal"
							@input="maxEdit"
							ref="maxInput"
							id="maxInput"
						/>
					</label>
				</div>
				<ui-button
					color="clear-white"
					class="info-button"
					@click="showInfo = !showInfo"
				>
					<i
						class="fas fa-question-circle"
						style="margin-right: 10px"
					></i>
					How does this work?
				</ui-button>
				<funFact title="Fun Fact"
					>The average RoundUp is ~$0.88/day or $26.84/month</funFact
				>
				<label>
					<ui-button
						native-type="submit"
						color="white"
						:loading="isLoading"
						>Set Goal</ui-button
					>
				</label>
			</form>
			<modal v-model="showInfo" size="small">
				<h4 style="font-size: 20px">Preferences</h4>
				<p style="font-size: 16px">
					If your total monthly RoundUps are greater than your
					minimum, you’ll donate your RoundUps. If they’re less,
					you’ll donate your minimum. <br />
					<br />If you set a maximum, your monthly RoundUps will never
					go above that amount. You can update these values in
					Settings any time.
				</p>
				<ui-button
					color="white"
					style="
						border: 1px solid #2553cd;
						display: flex;
						width: auto;
						height: 40px;
						border-radius: 20px;
						align-items: center;
						margin-right: 0;
						margin-left: auto;
						font-size: 16px;
					"
					@click="showInfo = !showInfo"
					>Close</ui-button
				>
			</modal>
		</screen>
	</div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { getUrlParams } from '../../utilities'
import VueSlider from 'vue-slider-component'
import UiButton from '../../ui/Button'
import UiInput from '../../ui/Input'
import UiHeader from '../../ui/Header'
import Screen from '../../ui/Screen'
import Stepper from '../../ui/Stepper'
import Modal from '../../ui/Modal'
import FunFact from '../../ui/FunFact'

export default {
	name: 'min-donation-view',
	components: {
		FunFact,
		UiButton,
		UiInput,
		UiHeader,
		Screen,
		Stepper,
		VueSlider,
		Modal
	},
	data() {
		return {
			isLoading: false,
			minDonation: '15.00',
			minMax: [20, 100],
			max: null,
			first_option: 0,
			onboarding: true, //this.$route.params.onboarding,
			maxEdited: false,
			errorMsg: '',
			showInfo: false,
			sliderMax: 100,
			invalidMin: false,
			invalidMax: false
		}
	},
	computed: {
		...mapGetters(['user', 'nextSignupStep', 'organizations', 'cause']),
		minGoal: {
			get() {
				return this.moneyFormat(this.minMax[0])
			},
			set(newValue) {
				this.invalidMin = false
				const value = parseFloat(newValue.replace(/[^0-9.]/g, ''))
				if (value && value > this.minMax[1]) {
					if (this.maxEdited) {
						this.minMax[1] = value
					} else {
						this.minMax[1] = value * 1.5
					}
					this.updateSliderMax()
				}
				if (value) {
					this.minMax[0] = value
					this.$refs.slider.setValue(this.minMax)
				} else {
					this.invalidMin = true
				}
			}
		},
		maxGoal: {
			get() {
				if (this.minMax[1] === this.sliderMax && !this.maxEdited) {
					return ''
				}

				return this.moneyFormat(this.minMax[1])
			},
			set(newValue) {
				this.invalidMax = false
				if (!newValue) {
					this.maxEdited = false
					return
				}
				const value = parseFloat(newValue.replace(/[^0-9.]/g, ''))
				if (value) {
					this.minMax[1] = value
					if (this.minMax[0] <= this.minMax[1]) {
						this.updateSliderMax()
						this.$refs.slider.setValue(this.minMax)
					}
					this.maxEdit()
				} else {
					this.invalidMax = true
				}
			}
		}
	},
	methods: {
		...mapActions(['updateUser', 'getInstitution', 'logout']),
		moneyFormat(v) {
			return `$${('' + v).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`
		},
		marks(val) {
			if (val === 1) {
				return {
					label: `$1`,
					labelStyle: {
						color: 'white'
					}
				}
			}
			if (val === this.sliderMax) {
				return {
					label: `$${this.sliderMax}+`,
					labelStyle: {
						color: 'white'
					}
				}
			}
			return false
		},
		error(type, msg) {
			switch (type) {
				case 4:
					return
			}
			this.errorMsg = msg
		},
		clearErrorMsg() {
			this.errorMsg = ''
		},
		trackChange(val) {
			if (val == 1) {
				this.invalidMax = false
				this.maxEdit()
			} else {
				this.invalidMin = false
			}
		},
		maxEdit() {
			this.maxEdited = true
		},
		async onSubmit() {
			this.isLoading = true
			if (this.invalidMin) {
				this.isLoading = false
				this.$swal(
					'Error',
					'Your minimum is not a valid number. Your minimum must be at least $1.00.'
				)
				return
			}
			if (this.invalidMax) {
				this.isLoading = false
				this.$swal('Error', 'Your maximum is not a valid number.')
				return
			}
			const minDonation = Math.round(this.minMax[0] * 100)
			var maxDonation
			if (this.maxEdited && document.getElementById('maxInput').value) {
				maxDonation = Math.round(this.minMax[1] * 100)
			}
			if (
				(!minDonation || minDonation < 1000) &&
				this.cause.type === 'group'
			) {
				this.isLoading = false
				this.$swal(
					'Error',
					'In order to effectively support multiple organizations, we require a minimum goal of at least $10/month. That’s only around $0.33/day!'
				)
				return
			}
			if (!minDonation || minDonation < 100) {
				this.isLoading = false
				this.$swal(
					'Error',
					'Your monthly minimum must be at least $1.00'
				)
				return
			}

			if (minDonation > 4900) {
				this.isLoading = false
				const result = await this.$swal({
					title: `Confirm Monthly Minimum`,
					text: `${this.moneyFormat(
						this.minMax[0]
					)} is very generous and ${
						this.cause.name
					} will be so grateful for your support! Please confirm to continue.`,
					showCancelButton: true,
					cancelButtonText: 'Cancel',
					allowOutsideClick: false,
					allowEscapeKey: false
				}).then((result) => {
					return result
				})
				if (result.dismiss === 'cancel') {
					return
				}
				this.isLoading = true
			}

			const user = {
				minimum_donation: minDonation,
				maximum_donation: maxDonation,
				email: this.user.email,
				whole_transaction_amount: this.user.whole_transaction_amount,
				phone: this.user.phone
			}

			try {
				window.analytics.track('set_monthly_minumum', {
					min: minDonation,
					default: 2000
				})
			} catch {
				null
			}

			await this.onUpdate(user)
		},
		onUpdate(user) {
			this.updateUser(user)
				.then(() => {
					this.isLoading = false
					this.navigate(
						{
							name: this.nextSignupStep,
							params: {
								onboarding: true,
								backRoute: 'mindonation',
								nextRoute: 'pre-accounts'
							}
						},
						'forward'
					)
				})
				.catch(({ message }) => {
					this.isLoading = false
					this.$swal('Error', message)
				})
		},
		onBack() {
			this.navigate(
				{ name: 'institutions', params: { onboarding: true } },
				'back'
			)
		},
		onLogout() {
			if (confirm('Would you like to log out?')) {
				this.logout().then(() => {
					this.$router.push('logout')
				})
			}
		},
		updateSliderMax() {
			var maxDefault = 100
			if (maxDefault < Math.max(...this.minMax)) {
				maxDefault = Math.max(...this.minMax)
			}
			if (this.minMax[0] > maxDefault) {
				this.sliderMax = this.minMax[0] * 1.5
			}
			this.sliderMax = maxDefault
		}
	},
	mounted() {
		const params = getUrlParams(window.location.href)
		if (params.amount) {
			this.minDonation = params.amount
		} else {
			const options = ['20.00', '25.00', '10.00']
			const random_pick =
				options[Math.floor(Math.random() * options.length)]
			this.first_option = random_pick
			this.minMax = [parseFloat(random_pick), this.minMax[1]]
		}
		if (this.user.minimum_donation) {
			this.minMax = [this.user.minimum_donation / 100, this.minMax[1]]
			this.minGoal = `${this.user.minimum_donation / 100}`
			this.$refs.slider.setValue(this.minMax)
		}
		if (this.user.maximum_donation) {
			this.minMax = [this.minMax[0], this.user.minimum_donation / 100]
			this.maxGoal = `${this.user.maximum_donation / 100}`
			this.$refs.slider.setValue(this.minMax)
		}
	}
}
</script>

<style lang="scss">
.min-donation-view {
	.roundup-slider {
		.vue-slider-process {
			background: $mint;
			border-radius: 15px;
		}
		.vue-slider-rail {
			border-radius: 15px;
			height: 13px;
			background: $purple;
		}
		.slider-dot {
			margin-top: -5px;
		}
	}
	.subtitle {
		color: rgba(255, 255, 255, 0.8);
		line-height: 20px;
		font-weight: 400;
		font-size: 16px;
	}
	.header-logo {
		background: #fff;
		height: 52px;
		width: 52px;
	}
}
</style>
